import { ChangeEvent, useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import ConfirmHeader from 'src/components/Headers/ConfirmationHeader';
import { ConfirmationDialogProps } from 'src/models/DialogBox.interface';
import { actionButtons, help } from 'src/utils/properties.utils';
import { CustomerStatus } from 'src/models/enums/CustomerStatus.enum';
import { PurchaseOrderStatus } from 'src/models/enums/PurchaseOrderStatus.enum';
import { BankStatus } from 'src/models/enums/BankStatus.enum';
import TextArea from 'src/components/InputFields/TextArea';
import {
  StatusLabel,
  ChevronIcon,
  StyledMenuItem,
  ActionWrapper,
  StyledActions,
  ConfirmButton,
  DialogContainer,
  StatusFieldContainer,
  FormControlStyled,
  ReasonFieldContainer,
} from './ConfirmationDialog.styles';
import {
  CloseIcon,
  StyledDialog,
  CancelButton,
  StyledSelect,
} from 'src/components/DialogBox/DialogBox.styles';
import { capitalizeFirstLetter } from 'src/utils/text.utils';

const ConfirmationDialog = <
  TStatus extends
    | PurchaseOrderStatus
    | CustomerStatus
    | BankStatus = CustomerStatus
>(
  props: ConfirmationDialogProps<TStatus>
) => {
  const {
    open,
    onClose,
    onConfirm,
    title,
    subtitle,
    confirmText,
    confirmButtonColor = 'var(--primary-blue)',
    alertIconColor = 'var(--warning)',
    width = '600px',
    height = '275px',
    reasonHeight = '60px',
    rows = 2,
    dialogType,
    status: initialStatus,
    onStatusChange,
    statusOptions = [
      CustomerStatus.ACTIVE,
      CustomerStatus.INACTIVE,
    ] as TStatus[],
    reason = '',
    paddingRight = '16px',
  } = props;

  const [status, setStatus] = useState<TStatus>(
    initialStatus || (CustomerStatus.ACTIVE as TStatus)
  );
  const [showReasonField, setShowReasonField] = useState<boolean>(
    initialStatus === CustomerStatus.SUSPENDED
  );
  const [reasonText, setReasonText] = useState<string>(reason);
  const [confirmBtnText, setConfirmBtnText] = useState<string>(confirmText);
  const [initialReason, setInitialReason] = useState<string | null>(reason);
  const [touched, setTouched] = useState<boolean>(false);
  const isStatusChanged = initialStatus !== status;
  const isReasonChanged = reasonText !== initialReason;
  const dialogHeight = showReasonField ? '520px' : height;

  useEffect(() => {
    if (open) {
      setStatus((initialStatus || CustomerStatus.ACTIVE) as TStatus);
      setShowReasonField(
        dialogType === actionButtons.UPDATE &&
          initialStatus === CustomerStatus.SUSPENDED
      );
      setReasonText(reason);
      setInitialReason(reason);
      setTouched(false);
      setConfirmBtnText(
        dialogType === actionButtons.UPDATE
          ? capitalizeFirstLetter(status)
          : confirmText
      );
    }
  }, [open, initialStatus, reason]);

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const newStatus = event.target.value as TStatus;
    setStatus(newStatus);
    setConfirmBtnText(capitalizeFirstLetter(newStatus));
    setShowReasonField(newStatus === CustomerStatus.SUSPENDED);
    onStatusChange?.(newStatus);
  };

  const handleReasonChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newReason = event.target.value;
    setReasonText(newReason);
    setTouched(true);
  };

  const validateReason = () => {
    return reasonText.trim().length === 0 && touched
      ? 'Reason ' + help.REQUIRED
      : null;
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      width={width}
      height={dialogHeight}
    >
      <DialogContainer paddingRight={paddingRight}>
        <ConfirmHeader
          iconColor={alertIconColor}
          title={title}
          subtitle={subtitle}
        />
        <CloseIcon component="i" className="bx bx-x-circle" onClick={onClose} />
        {dialogType === actionButtons.UPDATE && (
          <StatusFieldContainer>
            <StatusLabel>Status</StatusLabel>
            <FormControlStyled>
              <StyledSelect
                value={status}
                onChange={handleStatusChange}
                IconComponent={() => (
                  <ChevronIcon component="i" className="bx bxs-chevron-down" />
                )}
              >
                {statusOptions.map((option) => (
                  <StyledMenuItem key={option} value={option}>
                    {option}
                  </StyledMenuItem>
                ))}
              </StyledSelect>
            </FormControlStyled>
            {showReasonField && (
              <ReasonFieldContainer>
                <TextArea
                  rows={rows}
                  id="reason"
                  label="Reason"
                  value={reasonText}
                  onChange={handleReasonChange}
                  mt={2}
                  errorState={Boolean(validateReason())}
                  customValidation={validateReason}
                  height={reasonHeight}
                />
              </ReasonFieldContainer>
            )}
          </StatusFieldContainer>
        )}
        {dialogType === actionButtons.DELETE && (
          <StatusFieldContainer>
            <ReasonFieldContainer>
              <TextArea
                id="deleteReason"
                label="Reason for Deletion"
                value={reasonText}
                onChange={handleReasonChange}
                mt={2}
                errorState={Boolean(validateReason())}
                customValidation={validateReason}
              />
            </ReasonFieldContainer>
          </StatusFieldContainer>
        )}
      </DialogContainer>
      <ActionWrapper>
        <StyledActions>
          <ConfirmButton
            colorValue={confirmButtonColor}
            onClick={() => onConfirm(status, reasonText)}
            disabled={
              (dialogType === actionButtons.UPDATE &&
                ((!isStatusChanged &&
                  !(status === CustomerStatus.SUSPENDED && isReasonChanged)) ||
                  (status === CustomerStatus.SUSPENDED &&
                    reasonText.trim().length === 0))) ||
              (dialogType === actionButtons.DELETE &&
                reasonText.trim().length === 0)
            }
          >
            {confirmBtnText}
          </ConfirmButton>
          <CancelButton className="btn-inactive" onClick={onClose}>
            Cancel
          </CancelButton>
        </StyledActions>
      </ActionWrapper>
    </StyledDialog>
  );
};

export default ConfirmationDialog;
